/**
 * Switching the person's current view to a baseline, and back (issue #97, phase D).
 *
 * THE SAME MECHANISM AS STOPPING WORK: the server remembers which branch each
 * person is reading, `PATCH /api/projects/{slug}` sets it, and the ref
 * `baseline:<tag>` is simply a value that field can hold. Going back is that call
 * with the program's default branch. Both clear the remembered issue (`''`, since
 * an omitted field would be left alone and the bar would go on naming an issue the
 * person has left).
 *
 * Then three things the screens depend on: the program context is re-read (so
 * `branch` and every ref-keyed screen follow), the principal is re-read (the
 * server answers `can_write` false and a `baseline` while one is viewed, and a
 * stale principal would leave the editors live), and the person goes home, for
 * the reason swapping program and stopping work do - the screen they were on may
 * name something the other view does not have.
 *
 * One hook so the Baselines section, the banner and the work picker cannot
 * disagree about what "viewing a baseline" does.
 */

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, api } from '../../api'
import { useRefreshPrincipal } from '../../state/AppData'
import { baselineRef } from '../../state/baselineRef'
import { useProject } from '../../state/Project'
import { useToasts } from '../../state/Toasts'

export interface BaselineView {
  /** Make this baseline the current view. Resolves true when it did. */
  view: (tag: string) => Promise<boolean>
  /** Return to the program's default branch. Resolves true when it did. */
  back: () => Promise<boolean>
  /** A switch is in flight. */
  busy: boolean
}

export function useBaselineView(): BaselineView {
  const { current, reload } = useProject()
  const refreshPrincipal = useRefreshPrincipal()
  const navigate = useNavigate()
  const toasts = useToasts()
  const [busy, setBusy] = useState(false)

  const switchTo = useCallback(
    async (ref: string): Promise<boolean> => {
      if (!current) return false
      setBusy(true)
      try {
        await api.setProjectState(current.slug, { branch: ref, issue: '' })
        await reload()
        await refreshPrincipal()
        navigate('/')
        return true
      } catch (failure) {
        toasts.failed((failure as ApiError).message || 'Could not change the view')
        return false
      } finally {
        setBusy(false)
      }
    },
    [current, reload, refreshPrincipal, navigate, toasts],
  )

  const view = useCallback((tag: string) => switchTo(baselineRef(tag)), [switchTo])
  const back = useCallback(
    () => switchTo(current?.default_branch || 'main'),
    [switchTo, current],
  )

  return { view, back, busy }
}
